import { useState, useEffect } from "react";
import ArrivalScreen from "./pages/ArrivalScreen";
import GameScreen from "./pages/GameScreen";
import EndGamePage from "./pages/EndGamePage";
import StatsPage from "./pages/StatsPage";
import getDailyCity from "./game/dailyPuzzle";
import HowToPlay from "./pages/HowToPlay";
import CookieBanner from "./components/CookieBanner";
import { loadAnalytics, trackEvent } from "./utils/analytics";

function App() {
  const [stage, setStage] = useState("arrival");
  const [puzzleCity, setPuzzleCity] = useState(null);
  const [activeGame, setActiveGame] = useState(null);
  const [gameData, setGameData] = useState(null);
  const [showHowToPlay, setShowHowToPlay] = useState(false);

  // NEW: remembers which page the user was on before opening Stats
  const [statsPreviousStage, setStatsPreviousStage] = useState("arrival");

  // 1. FETCH AND RESTORE
  useEffect(() => {
    getDailyCity().then((city) => {
      setPuzzleCity(city);

      // Now that we have the puzzle, check for a saved game
      const savedGame = localStorage.getItem("flightleGame");
      const playedToday = localStorage.getItem("playedToday");

      if (savedGame && playedToday === new Date().toDateString()) {
        try {
          const parsed = JSON.parse(savedGame);

          // Robust check: Look at GameScreen's actual completion triggers
          const isWon =
            parsed.outcome === "win" ||
            (parsed.guesses && parsed.guesses.some((g) => g.correct));

          const isLost =
            parsed.outcome === "lose" ||
            (parsed.guesses && parsed.guesses.length >= 6);

          const isFinished =
            parsed.gameOver === true || isWon || isLost;

          if (isFinished) {
            // Re-inject the city data just in case they refreshed mid-animation
            const finalState = {
              ...parsed,
              gameOver: true,
              puzzle: city,
              puzzleCity: city.name,
            };

            setGameData(finalState);
            setActiveGame(finalState);
            setStage("end");
          } else {
            // Game is currently in progress
            setActiveGame(parsed);
            setStage("game");
          }
        } catch (e) {
          console.error("Failed to restore game state:", e);
        }
      }
    });
  }, []);

  // 2. AUTO-SAVE IN-PROGRESS GAME
  useEffect(() => {
    if (activeGame) {
      localStorage.setItem("flightleGame", JSON.stringify(activeGame));
      localStorage.setItem(
        "playedToday",
        new Date().toDateString()
      );
    }
  }, [activeGame]);

  // NEW: Open Stats and remember where the user came from
  const openStats = () => {
    setStatsPreviousStage(stage);
    setStage("stats");
  };

  // NEW: Return from Stats to the page the user was previously on
  const closeStats = () => {
    setStage(statsPreviousStage);
  };

  if (!puzzleCity) return <div>Loading...</div>;

  return (
    <div>
      {/* ARRIVAL */}
      {stage === "arrival" && (
        <ArrivalScreen
          puzzle={puzzleCity}
          onStart={() => {
            trackEvent("game_start", {
              puzzle_date: new Date().toISOString().split("T")[0],
            });

            if (!activeGame && gameData) {
              setActiveGame({
                guess: "",
                guesses: gameData.guesses || [],
                filtered: [],
                status: gameData.status || "",
                gameOver: true,
              });
            } else if (!activeGame) {
              // Brand new game setup
              setActiveGame({
                guess: "",
                guesses: [],
                filtered: [],
                status: "",
                gameOver: false,
              });
            }

            setStage("game");
          }}
          openHowToPlay={() => setShowHowToPlay(true)}
          onStats={openStats}
        />
      )}

      {/* GAME */}
      {stage === "game" && activeGame && (
        <GameScreen
          puzzle={puzzleCity}
          game={activeGame}
          setGame={setActiveGame}
          locked={!!gameData}
          onFinish={(data) => {
            trackEvent("game_complete", {
              result: data.outcome || "unknown",
              guesses_used: data.guesses?.length || 0,
            });

            setGameData(data);

            // Update activeGame state to fully completed
            const finalState = {
              ...activeGame,
              ...data,
              gameOver: true,
            };

            setActiveGame(finalState);

            // Explicitly save the finalized state
            localStorage.setItem(
              "flightleGame",
              JSON.stringify(finalState)
            );
            localStorage.setItem(
              "playedToday",
              new Date().toDateString()
            );

            setStage("end");
          }}
          goBack={() => setStage("arrival")}
          goToResults={() => setStage("end")}
          openHowToPlay={() => setShowHowToPlay(true)}
          onStats={openStats}
        />
      )}

      {/* END SCREEN */}
      {stage === "end" && gameData && (
        <EndGamePage
          puzzle={puzzleCity}
          gameData={gameData}
          onClose={() => {
            setActiveGame({
              guess: "",
              guesses: gameData.guesses || [],
              filtered: [],
              status: gameData.status || "",
              gameOver: true,
              showAllClues: true,
            });

            setStage("game");
          }}
          openHowToPlay={() => setShowHowToPlay(true)}
          onStats={openStats}
        />
      )}

      {/* STATS / ACHIEVEMENTS */}
      {stage === "stats" && (
        <StatsPage
          onBack={closeStats}
        />
      )}

      {/* HOW TO PLAY */}
      {showHowToPlay && (
        <HowToPlay
          onClose={() => setShowHowToPlay(false)}
        />
      )}

      {/* COOKIE BANNER */}
      <CookieBanner
        onAccept={() => {
          loadAnalytics();
        }}
      />
    </div>
  );
}

export default App;